import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Tag,
  Briefcase,
  MagnifyingGlass as Search,
  Plus,
  PencilSimple,
  Trash,
  X,
  Eye,
  EyeSlash,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  fetchPracticeAreas,
  createPracticeArea,
  updatePracticeArea,
  deletePracticeArea,
  type PracticeArea,
} from '@/lib/practiceAreasApi';
import { getApiErrorMessage } from '@/lib/api';
import { ScrollLock } from '@/components/shared/ScrollLock';

export default function PracticeAreasPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<PracticeArea | 'new' | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PracticeArea | null>(null);

  // Fetch active AND inactive so the admin manages both.
  const q = useQuery({ queryKey: ['practice-areas', 'all'], queryFn: () => fetchPracticeAreas({}) });
  const areas = q.data ?? [];

  const toggleMut = useMutation({
    mutationFn: (a: PracticeArea) => updatePracticeArea(a.id, { isActive: !a.isActive }),
    onSuccess: (_r, a) => { toast.success(a.isActive ? 'Deactivated' : 'Activated'); qc.invalidateQueries({ queryKey: ['practice-areas'] }); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not update')),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => deletePracticeArea(id),
    onSuccess: () => { toast.success('Practice area deleted'); qc.invalidateQueries({ queryKey: ['practice-areas'] }); setDeleteTarget(null); },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not delete')),
  });

  const lower = search.trim().toLowerCase();
  const filtered = useMemo(
    () => (lower ? areas.filter((a) => a.name.toLowerCase().includes(lower) || a.slug.toLowerCase().includes(lower)) : areas),
    [areas, lower],
  );

  return (
    <div>
      <PageHeader
        onBack={() => navigate(-1)}
        title="Practice areas"
        subtitle="Your chambers' own areas of law — used by cases, judgments and allocation. Add, rename, deactivate or remove."
        actions={
          <Button variant="primary" size="md" leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing('new')}>
            Add practice area
          </Button>
        }
      />

      <Card className="mb-4 p-3">
        <div className="flex items-center gap-2 rounded-md border border-border bg-card px-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input type="search" placeholder="Search practice areas…" value={search} onChange={(e) => setSearch(e.target.value)} className="h-9 flex-1 bg-transparent text-sm placeholder:text-muted-foreground focus:outline-none" />
        </div>
      </Card>

      {q.isLoading ? (
        <div className="py-16 text-center text-sm text-muted-foreground">Loading…</div>
      ) : filtered.length === 0 ? (
        <Card className="py-12 text-center"><p className="text-muted-foreground">{search ? `No areas match "${search}"` : 'No practice areas yet — add your first.'}</p></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((a) => (
            <Card key={a.id} className={a.isActive ? '' : 'opacity-70'}>
              <CardContent className="p-5">
                <div className="mb-3 flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600 dark:text-brand-400 dark:bg-brand-500/10">
                      <Tag className="h-5 w-5" />
                    </div>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-foreground">{a.name}</div>
                      {a.description && <div className="truncate text-xs text-muted-foreground">{a.description}</div>}
                    </div>
                  </div>
                  {a.isActive ? <Badge variant="success">Active</Badge> : <Badge>Inactive</Badge>}
                </div>

                <div className="flex items-center justify-between border-t border-border pt-3">
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1.5"><Briefcase className="h-3 w-3" /><span className="font-medium text-foreground">{a.caseCount}</span> cases</span>
                    <span><span className="font-medium text-foreground">{a.barristerCount}</span> barristers</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setEditing(a)} title="Edit" className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"><PencilSimple className="h-4 w-4" /></button>
                    <button onClick={() => toggleMut.mutate(a)} disabled={toggleMut.isPending} title={a.isActive ? 'Deactivate' : 'Activate'} className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground">
                      {a.isActive ? <EyeSlash className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button onClick={() => setDeleteTarget(a)} title="Delete" className="rounded p-1.5 text-muted-foreground hover:bg-red-50 dark:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400 dark:hover:bg-red-950/40"><Trash className="h-4 w-4" /></button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {editing && (
        <AreaEditor
          area={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { qc.invalidateQueries({ queryKey: ['practice-areas'] }); setEditing(null); }}
        />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={() => setDeleteTarget(null)}>
          <ScrollLock />
          <div className="my-auto w-full max-w-sm rounded-lg border border-border bg-card p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-foreground">Delete “{deleteTarget.name}”?</h3>
            <p className="mt-2 text-sm text-muted-foreground">
              {deleteTarget.caseCount > 0 || deleteTarget.barristerCount > 0
                ? `${deleteTarget.caseCount} case(s) will lose this tag and ${deleteTarget.barristerCount} barrister link(s) will be removed. `
                : ''}
              Consider deactivating instead if it's just no longer used. This cannot be undone.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)}>Cancel</Button>
              <Button variant="primary" size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => deleteMut.mutate(deleteTarget.id)} disabled={deleteMut.isPending}>
                {deleteMut.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AreaEditor({ area, onClose, onSaved }: { area: PracticeArea | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(area?.name ?? '');
  const [description, setDescription] = useState(area?.description ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (name.trim().length < 2) { setError('Name must be at least 2 characters'); return; }
    setSaving(true); setError(null);
    try {
      if (area) await updatePracticeArea(area.id, { name: name.trim(), description: description.trim() || undefined });
      else await createPracticeArea({ name: name.trim(), description: description.trim() || undefined });
      toast.success(area ? 'Practice area saved' : 'Practice area created');
      onSaved();
    } catch (err) { setError(getApiErrorMessage(err, 'Could not save')); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4" onClick={onClose}>
      <ScrollLock />
      <div className="my-auto w-full max-w-md rounded-lg border border-border bg-card shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <span className="text-sm font-semibold text-foreground">{area ? 'Edit practice area' : 'New practice area'}</span>
          <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted" aria-label="Close"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4 px-5 py-4">
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Court of Protection" className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-xs font-medium text-muted-foreground">Description (optional)</span>
            <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Short description" className="h-9 w-full rounded-lg border border-border bg-card px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-400" />
          </label>
          {error && <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">{error}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={save} disabled={saving}>{saving ? 'Saving…' : area ? 'Save' : 'Create'}</Button>
        </div>
      </div>
    </div>
  );
}
